// src/screens/PlanHolidaysScreen.jsx

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createHoliday, fetchHolidays, updateHoliday, deleteHoliday } from '../store/holidaySlice';
import { getDaysInMonth, format, startOfMonth, getDay } from 'date-fns';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SchoolList from '../components/ListOfSchools';

const PlanHolidaysScreen = () => {
  const dispatch = useDispatch();
  const { holidays, loading } = useSelector((state) => state.holiday);

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [schoolId, setSchoolId] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [holidayName, setHolidayName] = useState('');
  const [description, setDescription] = useState('');
  const [editingHolidayId, setEditingHolidayId] = useState(null);

  useEffect(() => {
    if (year) {
      dispatch(fetchHolidays({ year, month, schoolId }));
    }
  }, [year, month, schoolId, dispatch]);

  const handleDayClick = (day) => {
    const formattedDate = `${year}-${month}-${day.toString().padStart(2, '0')}`;
    setSelectedDate(formattedDate);

    const holiday = getHolidayForDay(day);
    if (holiday) {
      setHolidayName(holiday.name);
      setDescription(holiday.description);
      setEditingHolidayId(holiday.id);
    } else {
      setHolidayName('');
      setDescription('');
      setEditingHolidayId(null);
    }
  };

  const handleSaveHoliday = async () => {
    if (!selectedDate || !holidayName) {
      toast.error('Please select date and enter holiday name');
      return;
    }

    try {
      if (editingHolidayId) {
        await dispatch(updateHoliday({
          id: editingHolidayId,
          data: {
            holidayDate: selectedDate,
            name: holidayName,
            description,
            isPublicHoliday: false,
            schoolId: schoolId || null,
          }
        })).unwrap();
        toast.success('Holiday updated successfully');
      } else {
        await dispatch(createHoliday([{
          holidayDate: selectedDate,
          name: holidayName,
          description,
          isPublicHoliday: false,
          schoolId: schoolId || null,
        }])).unwrap();
        toast.success('Holiday created successfully');
      }
      resetForm();
      dispatch(fetchHolidays({ year, month, schoolId }));
    } catch (error) {
      toast.error(error.message || 'Action failed');
    }
  };

  const handleDeleteHoliday = async () => {
    if (editingHolidayId) {
      try {
        await dispatch(deleteHoliday(editingHolidayId)).unwrap();
        toast.success('Holiday deleted successfully');
        resetForm();
        dispatch(fetchHolidays({ year, month, schoolId }));
      } catch (error) {
        toast.error(error.message || 'Failed to delete');
      }
    }
  };

  const resetForm = () => {
    setSelectedDate(null);
    setHolidayName('');
    setDescription('');
    setEditingHolidayId(null);
  };

  const daysInMonth = getDaysInMonth(new Date(year, parseInt(month) - 1));
  const firstDayOfMonth = getDay(startOfMonth(new Date(year, parseInt(month) - 1))); // 0 (Sun) - 6 (Sat)

  const getHolidayForDay = (day) => {
    const formatted = `${year}-${month}-${day.toString().padStart(2, '0')}`;
    return holidays.find(h => h.holidayDate.startsWith(formatted));
  };

  const weeks = [];
  let days = [];

  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
    if (days.length === 7) {
      weeks.push(days);
      days = [];
    }
  }
  if (days.length > 0) {
    while (days.length < 7) {
      days.push(null);
    }
    weeks.push(days);
  }

  return (
    <div className="p-6">
      {/* Toast Container */}
      {/* Put this once at app level ideally */}
      
      <ToastContainer />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Plan Holidays</h2>
        <div className="text-right text-gray-500">{year}-{month}</div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6 items-center">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="p-2 border rounded">
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select value={month} onChange={(e) => setMonth(e.target.value)} className="p-2 border rounded">
          {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <div className="flex-1">
          <SchoolList value={schoolId} onChange={setSchoolId} />
        </div>
      </div>

      {/* Calendar */}
      <div className="grid grid-cols-7 gap-2 text-center mb-4 font-semibold text-gray-600">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2 mb-6">
        {weeks.map((week, idx) => (
          week.map((day, index) => {
            if (day === null) {
              return <div key={`${idx}-${index}`} className="p-4 border rounded bg-transparent" />;
            }
            const holiday = getHolidayForDay(day);
            return (
              <div
                key={`${idx}-${day}`}
                onClick={() => handleDayClick(day)}
                className={`p-2 border rounded cursor-pointer ${
                  holiday ? 'bg-green-300' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <div className="font-bold">{day}</div>
                {holiday && (
                  <div className="text-xs text-green-800">{holiday.name}</div>
                )}
              </div>
            );
          })
        ))}
      </div>

      {/* Add or Edit Holiday Form */}
      {selectedDate && (
        <div className="p-4 border rounded mb-6">
          <h3 className="text-lg font-semibold mb-2">{editingHolidayId ? 'Update' : 'Add'} Holiday for {selectedDate}</h3>
          <input
            type="text"
            value={holidayName}
            onChange={(e) => setHolidayName(e.target.value)}
            placeholder="Holiday Name"
            className="block w-full p-2 border rounded mb-2"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            className="block w-full p-2 border rounded mb-4"
          />
          <div className="flex gap-4">
            <button
              onClick={handleSaveHoliday}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex-1"
            >
              {editingHolidayId ? 'Update Holiday' : 'Save Holiday'}
            </button>
            {editingHolidayId && (
              <button
                onClick={handleDeleteHoliday}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 flex-1"
              >
                Delete Holiday
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanHolidaysScreen;
